import { useCallback, useEffect, useMemo, useState } from "react";
import AppTheme from "../../../theme/AppTheme";
import { dataGridCustomizations } from "../../../theme/customizations";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CssBaseline,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import EditIcon from "@mui/icons-material/Edit";
import {
  notifyError,
  notifySuccess,
} from "../../../components/ToastContainer/Toast/Toast";
import {
  createStoreDeal,
  deleteStoreDealHeroImage,
  deleteStoreDealHeroVideo,
  deleteStoreDealImage,
  deleteStoreDealVideo,
  getStoreDealHero,
  getStoreDealItemOptions,
  getStoreDealSection,
  getStoreDealStoreOptions,
  getStoreDeals,
  saveStoreDealSection,
  setStoreDealStatus,
  StoreDealHeroMedia,
  StoreDealItemOption,
  StoreDealPayload,
  StoreDealRow,
  StoreDealSection,
  StoreDealStoreOption,
  updateStoreDeal,
  uploadStoreDealHeroImage,
  uploadStoreDealHeroVideo,
  uploadStoreDealImage,
  uploadStoreDealVideo,
} from "../../../service/BuyerConfiguration/StoreDeals/storeDealService";

const IMAGE_MAX_MB = 2;
const VIDEO_MAX_MB = 10;
const IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp"];
const VIDEO_TYPES = ["video/mp4", "video/webm", "video/quicktime"];

/**
 * ₹99 Store.
 *
 * A deal is one real menu item sold at a Tazty-funded price: the customer pays
 * the deal price, the restaurant is billed and paid its full menu price, and
 * Tazty covers the gap. The menu price shown here is read live from the item,
 * so it is never typed in — that is what keeps Tazty from overpaying a store.
 *
 * Limits are per deal and optional: a daily ₹ budget (the deal reads "Sold out
 * for today" once spent) and a per-person daily plate limit.
 */

const emptyForm = {
  id: 0,
  store_id: 0,
  item_id: 0,
  deal_price: "",
  daily_budget: "",
  per_user_daily_limit: "",
  max_qty_per_order: "10",
  ends_at: "",
  sequence: "0",
  is_active: true,
  media_photo_seconds: "2",
  media_muted: true,
  media_loop: true,
};

type FormState = typeof emptyForm;

const toIsoOrNull = (localValue: string): string | null =>
  localValue ? new Date(localValue).toISOString() : null;

const toLocalInput = (iso: string | null): string => {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours(),
  )}:${pad(d.getMinutes())}`;
};

const StoreDeals = (props: { disableCustomTheme?: boolean }) => {
  const xThemeComponents = { ...dataGridCustomizations };

  const [rows, setRows] = useState<StoreDealRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");

  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [storeOptions, setStoreOptions] = useState<StoreDealStoreOption[]>([]);
  const [itemOptions, setItemOptions] = useState<StoreDealItemOption[]>([]);
  const [loadingItems, setLoadingItems] = useState(false);

  // Editable section labels (name/tagline/home row title) — the whole buyer app
  // reads these, so a rename here reflects there without an app release.
  const DEFAULT_SECTION: StoreDealSection = {
    name: "₹99 Store",
    tagline: "Great Food. Just ₹99!",
    home_title: "Today's deals",
  };
  const [section, setSection] = useState<StoreDealSection>(DEFAULT_SECTION);
  const [sectionOpen, setSectionOpen] = useState(false);
  const [sectionForm, setSectionForm] = useState<StoreDealSection>(DEFAULT_SECTION);
  const [savingSection, setSavingSection] = useState(false);

  useEffect(() => {
    getStoreDealSection()
      .then((s) => setSection(s))
      .catch(() => setSection(DEFAULT_SECTION));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openSectionEdit = () => {
    setSectionForm(section);
    setSectionOpen(true);
  };

  const saveSection = async () => {
    if (!sectionForm.name.trim()) {
      notifyError("The name cannot be empty");
      return;
    }
    setSavingSection(true);
    try {
      await saveStoreDealSection({
        name: sectionForm.name.trim(),
        tagline: sectionForm.tagline.trim(),
        home_title: sectionForm.home_title.trim(),
      });
      setSection({
        name: sectionForm.name.trim(),
        tagline: sectionForm.tagline.trim(),
        home_title: sectionForm.home_title.trim(),
      });
      notifySuccess("Store name updated — it will show across the buyer app");
      setSectionOpen(false);
    } catch (e: any) {
      notifyError(e?.response?.data?.message || "Could not update the name");
    } finally {
      setSavingSection(false);
    }
  };

  // ₹99 Store banner media (photo + clip inside the orange card on Home).
  // Uploaded as soon as a file is picked; each change is live on the app's
  // next load.
  const [heroOpen, setHeroOpen] = useState(false);
  const [hero, setHero] = useState<StoreDealHeroMedia>({
    hero_image_url: null,
    hero_video_url: null,
  });
  const [heroBusy, setHeroBusy] = useState<null | "image" | "video">(null);

  const openHero = async () => {
    setHeroOpen(true);
    try {
      setHero(await getStoreDealHero());
    } catch (e: any) {
      notifyError(e?.response?.data?.message || "Could not load the banner media");
    }
  };

  const runHero = async (
    kind: "image" | "video",
    action: () => Promise<StoreDealHeroMedia>,
    done: string,
  ) => {
    setHeroBusy(kind);
    try {
      setHero(await action());
      notifySuccess(done);
    } catch (e: any) {
      notifyError(e?.response?.data?.message || "Could not update the banner media");
    } finally {
      setHeroBusy(null);
    }
  };

  const pickHeroImage = (file: File | null) => {
    if (!file) return;
    if (!IMAGE_TYPES.includes(file.type)) {
      notifyError("Image must be a JPG, PNG or WebP");
      return;
    }
    if (file.size > IMAGE_MAX_MB * 1024 * 1024) {
      notifyError(`Image must be under ${IMAGE_MAX_MB} MB`);
      return;
    }
    runHero("image", () => uploadStoreDealHeroImage(file), "Banner photo updated");
  };

  const pickHeroVideo = (file: File | null) => {
    if (!file) return;
    if (!VIDEO_TYPES.includes(file.type)) {
      notifyError("Video must be MP4, WebM or MOV");
      return;
    }
    if (file.size > VIDEO_MAX_MB * 1024 * 1024) {
      notifyError(`Video must be under ${VIDEO_MAX_MB} MB`);
      return;
    }
    runHero("video", () => uploadStoreDealHeroVideo(file), "Banner video updated");
  };

  // Card media, uploaded after the deal row is saved (needs the id).
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [existingImageUrl, setExistingImageUrl] = useState<string | null>(null);
  const [existingVideoUrl, setExistingVideoUrl] = useState<string | null>(null);
  const [removeImage, setRemoveImage] = useState(false);
  const [removeVideo, setRemoveVideo] = useState(false);

  const resetMedia = () => {
    setImageFile(null);
    setVideoFile(null);
    setExistingImageUrl(null);
    setExistingVideoUrl(null);
    setRemoveImage(false);
    setRemoveVideo(false);
  };

  const fetchDeals = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getStoreDeals(search ? { search } : undefined);
      setRows(res?.data ?? res ?? []);
    } catch (e: any) {
      notifyError(e?.response?.data?.message || "Failed to load ₹99 Store deals");
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    fetchDeals();
  }, [fetchDeals]);

  useEffect(() => {
    if (!open) return;
    getStoreDealStoreOptions()
      .then((res) => setStoreOptions(res?.data ?? res ?? []))
      .catch(() => setStoreOptions([]));
  }, [open]);

  useEffect(() => {
    if (!open || !form.store_id) {
      setItemOptions([]);
      return;
    }
    setLoadingItems(true);
    getStoreDealItemOptions(form.store_id)
      .then((res) => setItemOptions(res?.data ?? res ?? []))
      .catch(() => setItemOptions([]))
      .finally(() => setLoadingItems(false));
  }, [open, form.store_id]);

  const pickedItem = useMemo(
    () => itemOptions.find((i) => i.id === form.item_id) ?? null,
    [itemOptions, form.item_id],
  );
  const menuPrice = pickedItem?.menu_price ?? 0;
  const dealPrice = Number(form.deal_price || 0);
  const taztyPays = menuPrice > dealPrice ? Number((menuPrice - dealPrice).toFixed(2)) : 0;

  const openCreate = () => {
    setForm(emptyForm);
    resetMedia();
    setOpen(true);
  };

  const openEdit = (row: StoreDealRow) => {
    setForm({
      id: row.id,
      store_id: row.store_id,
      item_id: row.item_id,
      deal_price: String(row.deal_price),
      daily_budget: row.daily_budget != null ? String(row.daily_budget) : "",
      per_user_daily_limit:
        row.per_user_daily_limit != null ? String(row.per_user_daily_limit) : "",
      max_qty_per_order: String(row.max_qty_per_order ?? 10),
      ends_at: toLocalInput(row.ends_at),
      sequence: String(row.sequence ?? 0),
      is_active: row.is_active,
      media_photo_seconds: String(row.media_photo_seconds ?? 2),
      media_muted: row.media_muted ?? true,
      media_loop: row.media_loop ?? true,
    });
    resetMedia();
    setExistingImageUrl(row.custom_image_url ?? null);
    setExistingVideoUrl(row.video_url ?? null);
    setOpen(true);
  };

  const pickImage = (file: File | null) => {
    if (!file) return;
    if (!IMAGE_TYPES.includes(file.type)) {
      notifyError("Image must be a JPG, PNG or WebP");
      return;
    }
    if (file.size > IMAGE_MAX_MB * 1024 * 1024) {
      notifyError(`Image must be under ${IMAGE_MAX_MB} MB`);
      return;
    }
    setImageFile(file);
    setRemoveImage(false);
  };

  const pickVideo = (file: File | null) => {
    if (!file) return;
    if (!VIDEO_TYPES.includes(file.type)) {
      notifyError("Video must be MP4, WebM or MOV");
      return;
    }
    if (file.size > VIDEO_MAX_MB * 1024 * 1024) {
      notifyError(`Video must be under ${VIDEO_MAX_MB} MB`);
      return;
    }
    setVideoFile(file);
    setRemoveVideo(false);
  };

  const handleToggle = async (row: StoreDealRow) => {
    try {
      await setStoreDealStatus(row.id, !row.is_active);
      notifySuccess(
        !row.is_active
          ? `"${row.item_name}" is now live in the ₹99 Store`
          : `"${row.item_name}" is hidden from the ₹99 Store`,
      );
      fetchDeals();
    } catch (e: any) {
      notifyError(e?.response?.data?.message || "Could not change the deal status");
    }
  };

  const handleSave = async () => {
    if (!form.store_id || !form.item_id) {
      notifyError("Pick a restaurant and an item");
      return;
    }
    if (!(dealPrice > 0) || dealPrice > 99) {
      notifyError("Deal price must be between ₹1 and ₹99");
      return;
    }
    if (menuPrice > 0 && dealPrice >= menuPrice) {
      notifyError(`Deal price must be below the menu price (₹${menuPrice})`);
      return;
    }

    const payload: StoreDealPayload = {
      store_id: form.store_id,
      item_id: form.item_id,
      deal_price: dealPrice,
      daily_budget: form.daily_budget ? Number(form.daily_budget) : null,
      per_user_daily_limit: form.per_user_daily_limit
        ? Number(form.per_user_daily_limit)
        : null,
      max_qty_per_order: Number(form.max_qty_per_order || 10),
      ends_at: toIsoOrNull(form.ends_at),
      sequence: Number(form.sequence || 0),
      is_active: form.is_active,
      media_photo_seconds: Number(form.media_photo_seconds || 2),
      media_muted: form.media_muted,
      media_loop: form.media_loop,
    };

    setSaving(true);
    try {
      let dealId = form.id;
      if (form.id) {
        await updateStoreDeal(form.id, payload);
      } else {
        const res: any = await createStoreDeal(payload);
        const created = res?.data ?? res;
        dealId = created?.id ?? created?.data?.id;
      }

      // Media is a second step because it needs the deal id. A media failure
      // must not lose the saved deal, so it is reported but does not throw.
      if (dealId) {
        try {
          if (removeImage && !imageFile) await deleteStoreDealImage(dealId);
          if (imageFile) await uploadStoreDealImage(dealId, imageFile);
          if (removeVideo && !videoFile) await deleteStoreDealVideo(dealId);
          if (videoFile) await uploadStoreDealVideo(dealId, videoFile);
        } catch (mediaErr: any) {
          notifyError(
            mediaErr?.response?.data?.message ||
              "The deal was saved, but its image/video could not be uploaded. Edit the deal to try again.",
          );
        }
      }

      notifySuccess(form.id ? "Deal updated" : "Deal created");
      setOpen(false);
      fetchDeals();
    } catch (e: any) {
      notifyError(e?.response?.data?.message || "Could not save the deal");
    } finally {
      setSaving(false);
    }
  };

  const columns: GridColDef[] = [
    {
      field: "item_name",
      headerName: "Item",
      flex: 1.4,
      minWidth: 200,
      renderCell: (p) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {p.row.image_url ? (
            <img
              src={p.row.image_url}
              alt=""
              style={{ width: 32, height: 32, borderRadius: 6, objectFit: "cover" }}
            />
          ) : null}
          <span>{p.row.item_name ?? `#${p.row.item_id}`}</span>
        </Box>
      ),
    },
    { field: "store_name", headerName: "Restaurant", flex: 1.1, minWidth: 160 },
    {
      field: "deal_price",
      headerName: "Price",
      width: 140,
      renderCell: (p) => (
        <Box sx={{ display: "flex", gap: 0.75, alignItems: "center" }}>
          <strong>₹{Number(p.row.deal_price)}</strong>
          <span style={{ textDecoration: "line-through", opacity: 0.55 }}>
            ₹{Number(p.row.menu_price)}
          </span>
        </Box>
      ),
    },
    {
      field: "tazty_pays_per_plate",
      headerName: "Tazty pays",
      width: 110,
      renderCell: (p) => `₹${Number(p.row.tazty_pays_per_plate)}`,
    },
    {
      field: "today_spend",
      headerName: "Today",
      width: 170,
      renderCell: (p) => (
        <span>
          ₹{Number(p.row.today_spend)}
          {p.row.daily_budget != null ? ` / ₹${Number(p.row.daily_budget)}` : ""}
          {` · ${p.row.today_orders} order${p.row.today_orders === 1 ? "" : "s"}`}
        </span>
      ),
    },
    {
      field: "limits",
      headerName: "Limits",
      width: 150,
      renderCell: (p) => (
        <span>
          {p.row.per_user_daily_limit != null
            ? `${p.row.per_user_daily_limit}/person/day`
            : "No person limit"}
        </span>
      ),
    },
    {
      field: "total_spend",
      headerName: "Total spend",
      width: 120,
      renderCell: (p) => `₹${Number(p.row.total_spend)}`,
    },
    {
      field: "is_active",
      headerName: "Status",
      width: 150,
      renderCell: (p) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Switch
            size="small"
            checked={!!p.row.is_active}
            onChange={() => handleToggle(p.row)}
          />
          {!p.row.is_price_valid ? (
            <Tooltip title="The menu price is no longer above the deal price, so this deal is hidden from the app">
              <Chip size="small" color="error" label="Price" />
            </Tooltip>
          ) : p.row.is_ended ? (
            <Chip size="small" label="Ended" />
          ) : null}
        </Box>
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 90,
      sortable: false,
      renderCell: (p) => (
        <Tooltip title="Edit">
          <IconButton size="small" onClick={() => openEdit(p.row)}>
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  return (
    <AppTheme {...props} themeComponents={xThemeComponents}>
      <CssBaseline enableColorScheme />
      <Box sx={{ width: "100%", maxWidth: { sm: "100%", md: "1700px" }, p: 2 }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          sx={{ mb: 2, alignItems: { sm: "center" }, justifyContent: "space-between" }}
        >
          <Box>
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Typography variant="h5">{section.name}</Typography>
              <Tooltip title="Rename — updates the whole buyer app">
                <IconButton size="small" onClick={openSectionEdit}>
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
            <Typography variant="body2" sx={{ opacity: 0.7 }}>
              Tazty pays the difference between the deal price and the restaurant's
              menu price.
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <TextField
              size="small"
              placeholder="Search item or restaurant"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <Button variant="outlined" onClick={openHero}>
              Banner media
            </Button>
            <Button variant="contained" onClick={openCreate}>
              Add deal
            </Button>
          </Stack>
        </Stack>

        <DataGrid
          rows={rows}
          columns={columns}
          loading={loading}
          getRowId={(r) => r.id}
          autoHeight
          disableRowSelectionOnClick
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
          pageSizeOptions={[25, 50, 100]}
        />
      </Box>

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{form.id ? "Edit deal" : "Add ₹99 Store deal"}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Autocomplete
              options={storeOptions}
              getOptionLabel={(o) =>
                [o.name, o.locality].filter(Boolean).join(", ") + ` (#${o.id})`
              }
              isOptionEqualToValue={(o, v) => o.id === v.id}
              value={storeOptions.find((s) => s.id === form.store_id) ?? null}
              onChange={(_, picked) =>
                setForm((f) => ({
                  ...f,
                  store_id: picked?.id ?? 0,
                  // Items belong to one restaurant; drop the previous pick.
                  item_id: 0,
                }))
              }
              renderInput={(params) => (
                <TextField {...params} required label="Restaurant" />
              )}
            />

            <Autocomplete
              options={itemOptions}
              loading={loadingItems}
              disabled={!form.store_id}
              getOptionLabel={(o) => `${o.name} — ₹${o.menu_price} (#${o.id})`}
              isOptionEqualToValue={(o, v) => o.id === v.id}
              value={pickedItem}
              onChange={(_, picked) =>
                setForm((f) => ({ ...f, item_id: picked?.id ?? 0 }))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  required
                  label="Menu item"
                  helperText={
                    form.store_id
                      ? "The menu price shown is the live price; the customer sees it struck through"
                      : "Pick a restaurant first"
                  }
                />
              )}
            />

            <Stack direction="row" spacing={2}>
              <TextField
                required
                fullWidth
                label="Deal price (₹)"
                type="number"
                inputProps={{ min: 1, max: 99, step: 1 }}
                value={form.deal_price}
                onChange={(e) => setForm((f) => ({ ...f, deal_price: e.target.value }))}
                helperText="₹99 or less, and below the menu price"
              />
              <TextField
                fullWidth
                label="Max per order"
                type="number"
                inputProps={{ min: 1, max: 10, step: 1 }}
                value={form.max_qty_per_order}
                onChange={(e) =>
                  setForm((f) => ({ ...f, max_qty_per_order: e.target.value }))
                }
                helperText="1 to 10 plates"
              />
            </Stack>

            {pickedItem ? (
              <Alert severity={taztyPays > 0 ? "info" : "warning"}>
                {taztyPays > 0
                  ? `Tazty pays ₹${taztyPays} per plate. The restaurant is paid its full ₹${menuPrice}.`
                  : "The deal price must be below the menu price."}
              </Alert>
            ) : null}

            <Stack direction="row" spacing={2}>
              <TextField
                fullWidth
                label="Daily budget (₹)"
                type="number"
                inputProps={{ min: 1, step: 1 }}
                value={form.daily_budget}
                onChange={(e) => setForm((f) => ({ ...f, daily_budget: e.target.value }))}
                helperText="Blank = unlimited. Shows 'Sold out for today' once spent"
              />
              <TextField
                fullWidth
                label="Per person per day"
                type="number"
                inputProps={{ min: 1, step: 1 }}
                value={form.per_user_daily_limit}
                onChange={(e) =>
                  setForm((f) => ({ ...f, per_user_daily_limit: e.target.value }))
                }
                helperText="Blank = unlimited plates per customer"
              />
            </Stack>

            <Stack direction="row" spacing={2}>
              <TextField
                fullWidth
                label="Ends on"
                type="datetime-local"
                value={form.ends_at}
                onChange={(e) => setForm((f) => ({ ...f, ends_at: e.target.value }))}
                InputLabelProps={{ shrink: true }}
                helperText="Blank = runs until switched off"
              />
              <TextField
                fullWidth
                label="Order on page"
                type="number"
                inputProps={{ min: 0, step: 1 }}
                value={form.sequence}
                onChange={(e) => setForm((f) => ({ ...f, sequence: e.target.value }))}
                helperText="Lower shows first"
              />
            </Stack>

            <Divider textAlign="left">
              <Typography variant="caption" sx={{ opacity: 0.7 }}>
                Card media (optional)
              </Typography>
            </Divider>
            <Typography variant="caption" sx={{ opacity: 0.7, mt: -1 }}>
              On the buyer's deal card the photo shows, then the video plays,
              then it loops. Leave blank to just use the item's photo.
            </Typography>

            <Stack direction="row" spacing={2}>
              {/* Image override */}
              <Box sx={{ flex: 1 }}>
                <Button component="label" variant="outlined" fullWidth sx={{ textTransform: "none" }}>
                  {imageFile ? "Change image" : existingImageUrl ? "Replace image" : "Choose image"}
                  <input
                    hidden
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={(e) => pickImage(e.target.files?.[0] ?? null)}
                  />
                </Button>
                <Typography variant="caption" sx={{ display: "block", opacity: 0.7, mt: 0.5 }}>
                  Overrides the item photo · JPG/PNG/WebP · ≤{IMAGE_MAX_MB} MB
                </Typography>
                {imageFile ? (
                  <Typography variant="caption" sx={{ display: "block" }}>
                    Selected: {imageFile.name}
                  </Typography>
                ) : existingImageUrl && !removeImage ? (
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                    <img
                      src={existingImageUrl}
                      alt=""
                      style={{ width: 40, height: 40, borderRadius: 6, objectFit: "cover" }}
                    />
                    <Button size="small" color="error" onClick={() => setRemoveImage(true)}>
                      Remove
                    </Button>
                  </Stack>
                ) : removeImage ? (
                  <Typography variant="caption" color="error" sx={{ display: "block" }}>
                    Will remove custom image (item photo shown)
                  </Typography>
                ) : null}
              </Box>

              {/* Promo video */}
              <Box sx={{ flex: 1 }}>
                <Button component="label" variant="outlined" fullWidth sx={{ textTransform: "none" }}>
                  {videoFile ? "Change video" : existingVideoUrl ? "Replace video" : "Choose video"}
                  <input
                    hidden
                    type="file"
                    accept="video/mp4,video/webm,video/quicktime"
                    onChange={(e) => pickVideo(e.target.files?.[0] ?? null)}
                  />
                </Button>
                <Typography variant="caption" sx={{ display: "block", opacity: 0.7, mt: 0.5 }}>
                  Promo clip · MP4/WebM/MOV · ≤{VIDEO_MAX_MB} MB
                </Typography>
                {videoFile ? (
                  <Typography variant="caption" sx={{ display: "block" }}>
                    Selected: {videoFile.name}
                  </Typography>
                ) : existingVideoUrl && !removeVideo ? (
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                    <Chip size="small" label="Video attached" />
                    <Button size="small" color="error" onClick={() => setRemoveVideo(true)}>
                      Remove
                    </Button>
                  </Stack>
                ) : removeVideo ? (
                  <Typography variant="caption" color="error" sx={{ display: "block" }}>
                    Will remove video
                  </Typography>
                ) : null}
              </Box>
            </Stack>

            <Stack direction="row" spacing={2} alignItems="center">
              <TextField
                label="Photo seconds"
                type="number"
                inputProps={{ min: 0, max: 10, step: 1 }}
                value={form.media_photo_seconds}
                onChange={(e) =>
                  setForm((f) => ({ ...f, media_photo_seconds: e.target.value }))
                }
                helperText="Photo hold before video"
                sx={{ width: 160 }}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={form.media_muted}
                    onChange={(e) => setForm((f) => ({ ...f, media_muted: e.target.checked }))}
                  />
                }
                label="Muted"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={form.media_loop}
                    onChange={(e) => setForm((f) => ({ ...f, media_loop: e.target.checked }))}
                  />
                }
                label="Loop"
              />
            </Stack>

            <Divider />

            <FormControlLabel
              control={
                <Switch
                  checked={form.is_active}
                  onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))}
                />
              }
              label="Live in the ₹99 Store"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? "Saving…" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={heroOpen}
        onClose={() => heroBusy === null && setHeroOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>{section.name} banner media</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ opacity: 0.7, mb: 2 }}>
            Plays inside the orange {section.name} card on the buyer home screen:
            the photo shows first, then the video plays (muted) and loops. With
            nothing uploaded the card stays plain orange. Changes appear on the
            app's next load.
          </Typography>

          <Typography sx={{ fontWeight: 600, mb: 1 }}>Photo</Typography>
          {hero.hero_image_url ? (
            <Box
              component="img"
              src={hero.hero_image_url}
              alt="Banner photo"
              sx={{ width: "100%", maxHeight: 160, objectFit: "cover", borderRadius: 2, mb: 1 }}
            />
          ) : (
            <Typography variant="body2" sx={{ opacity: 0.6, mb: 1 }}>
              No photo
            </Typography>
          )}
          <Stack direction="row" spacing={1} sx={{ mb: 3 }}>
            <Button variant="outlined" component="label" disabled={heroBusy !== null}>
              {heroBusy === "image"
                ? "Uploading…"
                : hero.hero_image_url
                  ? "Replace photo"
                  : "Upload photo"}
              <input
                hidden
                type="file"
                accept={IMAGE_TYPES.join(",")}
                onChange={(e) => {
                  pickHeroImage(e.target.files?.[0] ?? null);
                  e.target.value = "";
                }}
              />
            </Button>
            {hero.hero_image_url ? (
              <Button
                color="error"
                disabled={heroBusy !== null}
                onClick={() =>
                  runHero("image", deleteStoreDealHeroImage, "Banner photo removed")
                }
              >
                Remove
              </Button>
            ) : null}
          </Stack>

          <Typography sx={{ fontWeight: 600, mb: 1 }}>Video</Typography>
          {hero.hero_video_url ? (
            <Box
              component="video"
              src={hero.hero_video_url}
              muted
              loop
              autoPlay
              playsInline
              sx={{ width: "100%", maxHeight: 200, borderRadius: 2, mb: 1, background: "#000" }}
            />
          ) : (
            <Typography variant="body2" sx={{ opacity: 0.6, mb: 1 }}>
              No video
            </Typography>
          )}
          <Stack direction="row" spacing={1}>
            <Button variant="outlined" component="label" disabled={heroBusy !== null}>
              {heroBusy === "video"
                ? "Uploading…"
                : hero.hero_video_url
                  ? "Replace video"
                  : "Upload video"}
              <input
                hidden
                type="file"
                accept={VIDEO_TYPES.join(",")}
                onChange={(e) => {
                  pickHeroVideo(e.target.files?.[0] ?? null);
                  e.target.value = "";
                }}
              />
            </Button>
            {hero.hero_video_url ? (
              <Button
                color="error"
                disabled={heroBusy !== null}
                onClick={() =>
                  runHero("video", deleteStoreDealHeroVideo, "Banner video removed")
                }
              >
                Remove
              </Button>
            ) : null}
          </Stack>
          <Typography variant="caption" sx={{ opacity: 0.6, display: "block", mt: 1 }}>
            Photo: JPG/PNG/WebP up to {IMAGE_MAX_MB} MB. Video: MP4/WebM/MOV up to{" "}
            {VIDEO_MAX_MB} MB — keep it short (5–10 s) so it loads fast on mobile data.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHeroOpen(false)} disabled={heroBusy !== null}>
            Done
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={sectionOpen} onClose={() => setSectionOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Rename the store</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ opacity: 0.7, mb: 2 }}>
            These labels show across the buyer app — the page title, the home
            banner and the deals row. Changes appear on the app's next load.
          </Typography>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              autoFocus
              label="Name"
              value={sectionForm.name}
              onChange={(e) => setSectionForm((s) => ({ ...s, name: e.target.value }))}
              helperText="e.g. ₹99 Store, Offer Zone — used for the page title and banner"
            />
            <TextField
              label="Tagline"
              value={sectionForm.tagline}
              onChange={(e) => setSectionForm((s) => ({ ...s, tagline: e.target.value }))}
              helperText="Small line under the banner name"
            />
            <TextField
              label="Home row title"
              value={sectionForm.home_title}
              onChange={(e) =>
                setSectionForm((s) => ({ ...s, home_title: e.target.value }))
              }
              helperText="Heading above the deals row on the home screen"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSectionOpen(false)} disabled={savingSection}>
            Cancel
          </Button>
          <Button variant="contained" onClick={saveSection} disabled={savingSection}>
            {savingSection ? "Saving…" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </AppTheme>
  );
};

export default StoreDeals;
